import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../Layout';
import { useAuth } from '../../auth/AuthContext';

const API = 'https://api.kallabakari.is';

const MONTHS_IS = ['Janúar','Febrúar','Mars','Apríl','Maí','Júní','Júlí','Ágúst','September','Október','Nóvember','Desember'];
const DAYS_IS = ['Mán','Þri','Mið','Fim','Fös','Lau','Sun'];

const formatISK = n => new Intl.NumberFormat('is-IS', { style: 'currency', currency: 'ISK' }).format(n);

function formatProductLine(p) {
  switch (p.type) {
    case 'cake': return `${p.details.cake}${p.details.size ? ` (${p.details.size})` : ''}`;
    case 'bread': return `${p.details.bread} × ${p.details.quantity}`;
    case 'minidonut': return `Minidonuts × ${p.details.quantity}`;
    case 'bite': return `${p.details.name} × ${p.details.quantity}`;
    default: return `${p.details.other || p.details.name || 'Vara'} × ${p.details.quantity ?? 1}`;
  }
}

function statusBadge(status) {
  const map = {
    pending:   'bg-yellow-50 text-yellow-700 border-yellow-200',
    confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-red-50 text-red-600 border-red-200',
  };
  const labels = { pending: 'Í bið', confirmed: 'Staðfest', completed: 'Lokið', cancelled: 'Afbókað' };
  return (
    <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full border ${map[status] || ''}`}>
      {labels[status] || status}
    </span>
  );
}

export default function Calendar() {
  const { token, role } = useAuth();
  const isAdmin = role === 'admin';
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth()); // 0-based
  const [selectedDay, setSelectedDay] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/api/company/admin/all-orders`, { headers: { Authorization: token } })
        .then(res => {
          const regular = (res.data.regular || []).map(o => ({ ...o, _orderType: 'regular' }));
          const company = (res.data.company || []).map(o => ({ ...o, _orderType: 'company' }));
          setOrders([...regular, ...company]);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      axios.get(`${API}/api/company/orders`, { headers: { Authorization: token } })
        .then(res => setOrders(res.data.map(o => ({ ...o, _orderType: 'company' }))))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [token, isAdmin]);

  // Map date string YYYY-MM-DD → [orders]
  const ordersByDay = useMemo(() => {
    const map = {};
    orders.forEach(o => {
      const key = new Date(o.date).toISOString().split('T')[0];
      if (!map[key]) map[key] = [];
      map[key].push(o);
    });
    return map;
  }, [orders]);

  const selectedOrders = useMemo(() => {
    if (!selectedDay) return [];
    return ordersByDay[selectedDay] || [];
  }, [selectedDay, ordersByDay]);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    // Monday-first: 0=Mon, 6=Sun
    let startOffset = firstDay.getDay() - 1;
    if (startOffset < 0) startOffset = 6;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [year, month]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const handleDelete = async (orderId) => {
    if (!window.confirm('Eyða þessari pöntun?')) return;
    setDeletingId(orderId);
    try {
      await axios.delete(`${API}/api/company/orders/${orderId}`, { headers: { Authorization: token } });
      setOrders(prev => prev.filter(o => o._id !== orderId));
      setSelectedDay(null);
    } catch {
      alert('Ekki tókst að eyða pöntun');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Dagbók pantana</h1>
        <button
          onClick={() => navigate('/order/new')}
          className="bg-stone-500 hover:bg-stone-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          + Ný pöntun
        </button>
      </div>

      <div className="flex gap-6 items-start">
        {/* Calendar */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Month nav */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <button onClick={prevMonth} className="text-gray-400 hover:text-gray-700 px-2 py-1 rounded transition-colors">‹</button>
            <span className="text-sm font-semibold text-gray-800">{MONTHS_IS[month]} {year}</span>
            <button onClick={nextMonth} className="text-gray-400 hover:text-gray-700 px-2 py-1 rounded transition-colors">›</button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-gray-100">
            {DAYS_IS.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} className="border-b border-r border-gray-50 h-16" />;
              const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayOrders = ordersByDay[key] || [];
              const isToday = key === todayStr;
              const isSelected = key === selectedDay;
              const isPast = key < todayStr;

              return (
                <button
                  key={key}
                  onClick={() => setSelectedDay(isSelected ? null : key)}
                  className={`h-16 border-b border-r border-gray-50 flex flex-col items-start px-2 pt-1.5 text-left transition-colors ${
                    isSelected ? 'bg-stone-50 border-stone-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-stone-500 text-white' : isPast ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {day}
                  </span>
                  {dayOrders.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-0.5">
                      {dayOrders.slice(0, 3).map(o => (
                        <span key={o._id} className={`w-2 h-2 rounded-full ${
                          o.status === 'completed' ? 'bg-emerald-400' :
                          o.status === 'cancelled' ? 'bg-red-300' :
                          o.status === 'confirmed' ? 'bg-blue-400' : 'bg-stone-400'
                        }`} />
                      ))}
                      {dayOrders.length > 3 && (
                        <span className="text-gray-400 text-xs">+{dayOrders.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Side panel */}
        <div className="w-80 flex-shrink-0">
          {selectedDay ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-800">
                  {new Date(selectedDay + 'T12:00:00').toLocaleDateString('is-IS', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h2>
                <button onClick={() => setSelectedDay(null)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
              </div>

              {selectedOrders.length === 0 ? (
                <p className="text-xs text-gray-400">Engar pantanir þennan dag.</p>
              ) : (
                <div className="space-y-3">
                  {selectedOrders.map(order => (
                    <div key={order._id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {order._orderType === 'regular' ? (
                            <>
                              <p className="text-xs font-semibold text-gray-800 truncate">{order.name}</p>
                              <span className="text-xs text-gray-400">Vefpöntun</span>
                            </>
                          ) : (
                            <>
                              {order.companyId?.name && (
                                <p className="text-xs font-semibold text-gray-800 truncate">{order.companyId.name}</p>
                              )}
                              {statusBadge(order.status)}
                            </>
                          )}
                          {order.pickupTime && (
                            <p className="text-xs font-semibold text-stone-600 mt-0.5">
                              {order._orderType === 'company'
                                ? `${order.deliveryType === 'delivery' ? 'Sent' : 'Sótt'} kl. ${order.pickupTime}`
                                : `Sótt kl. ${order.pickupTime}`}
                            </p>
                          )}
                        </div>
                        <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{formatISK(order.totalAmount)}</span>
                      </div>

                      <ul className="space-y-0.5">
                        {order.products.map((p, i) => (
                          <li key={i} className="text-xs text-gray-600">{formatProductLine(p)}</li>
                        ))}
                      </ul>

                      {order.note && (
                        <p className="text-xs text-gray-400 italic">"{order.note}"</p>
                      )}

                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => navigate(`/order/${order._id}`)}
                          className="text-xs text-stone-700 hover:text-stone-900 font-medium transition-colors"
                        >
                          Skoða →
                        </button>
                        <button
                          onClick={() => handleDelete(order._id)}
                          disabled={deletingId === order._id}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors ml-auto"
                        >
                          Eyða
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedDay >= todayStr && (
                <button
                  onClick={() => navigate(`/order/new?date=${selectedDay}`)}
                  className="mt-4 w-full text-xs text-center text-stone-600 hover:text-stone-800 font-medium py-2 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  + Bæta við pöntun þennan dag
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 text-center">
              <p className="text-xs text-gray-400">Smelltu á dag til að sjá pantanir</p>
              {loading && <p className="text-xs text-gray-300 mt-2">Hleður pantanir...</p>}
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Skýring</p>
            <div className="space-y-1.5">
              {[
                { color: 'bg-stone-400', label: 'Í bið' },
                { color: 'bg-blue-400',  label: 'Staðfest' },
                { color: 'bg-emerald-400', label: 'Lokið' },
                { color: 'bg-red-300',   label: 'Afbókað' },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${color}`} />
                  <span className="text-xs text-gray-500">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
